import { useMemo } from 'react'

import { useTrackableValue } from '@app/packages/reactives/useTrackableValue'

import { useEvent } from '@app/hooks/useEvent'
import { useMountEffect } from '@app/hooks/useMountEffect'

import type { TaskContext, TaskWorker } from './Task'
import { Task } from './Task'

export function useRawTask<S, T extends TaskWorker>(task: Task<S, T>) {
  const value = useTrackableValue(task.value)
  const error = useTrackableValue(task.error)
  const loading = useTrackableValue(task.loading)
  const loadedAt = useTrackableValue(task.loadedAt)
  const state = useTrackableValue(task.state)

  return { value, error, loading, loadedAt, task, state }
}

export function useTaskWithState<S, T extends TaskWorker>(initialState: () => S, loader: (ctx: TaskContext<S>) => T) {
  const eloader = useEvent(loader)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const task = useMemo(() => Task.create(eloader, initialState), [eloader])

  useMountEffect(() => {
    return () => {
      task.abort()
    }
  })

  return useRawTask(task)
}

export function useTask<T extends TaskWorker>(loader: (ctx: TaskContext<undefined>) => T) {
  return useTaskWithState(() => undefined, loader)
}
